import styled from '@emotion/styled';
import { FC } from 'react';
import { KpiState } from '../../../helpers/kpi';
import { theme } from '../../../theme';

export type KpiBarVariant = 'scale' | 'nps';

export type KpiBarProps = {
  /** Raw metric value. */
  value: number;
  /** Lower bound of the value range (default 0). */
  min?: number;
  /** Upper bound of the value range (default 100). */
  max?: number;
  /**
   * `scale` renders a single-colour progress fill whose colour is driven by
   * `state`. `nps` renders a static red→yellow→green gauge track with a thumb
   * positioned at the value.
   */
  variant?: KpiBarVariant;
  /** Fill state for the `scale` variant. Ignored for `nps`. */
  state?: KpiState;
  /** Track thickness in px. */
  height?: number;
  className?: string;
  'data-cy'?: string;
};

const DEFAULT_HEIGHT = 8;

const clampRatio = (value: number, min: number, max: number) => {
  const span = max - min;
  if (span === 0) {
    return 0;
  }
  return Math.min(Math.max((value - min) / span, 0), 1);
};

export const getKpiBarColor = (state: KpiState) => {
  switch (state) {
    case 'success':
      return theme.colors.emotionSuccess.default;
    case 'warning':
      return theme.colors.emotionWarning.default;
    case 'danger':
      return theme.colors.emotionDanger.default;
    case 'normal':
    default:
      return theme.colors.brand.primary;
  }
};

const NPS_GRADIENT = `linear-gradient(90deg, ${theme.colors.emotionDanger.default} 0%, ${theme.colors.emotionWarning.default} 50%, ${theme.colors.emotionSuccess.default} 100%)`;

export const KpiBar: FC<KpiBarProps> = ({
  value,
  min = 0,
  max = 100,
  variant = 'scale',
  state = 'success',
  height = DEFAULT_HEIGHT,
  className,
  'data-cy': dataCy,
}) => {
  const ratio = clampRatio(value, min, max);

  if (variant === 'nps') {
    return (
      <Track height={height} className={className} data-cy={dataCy}>
        <GaugeFill height={height} />
        <Thumb height={height} style={{ left: `${ratio * 100}%` }} data-cy={dataCy ? `${dataCy}-thumb` : undefined} />
      </Track>
    );
  }

  return (
    <Track height={height} className={className} data-cy={dataCy}>
      <ScaleFill height={height} style={{ width: `${ratio * 100}%`, backgroundColor: getKpiBarColor(state) }} />
    </Track>
  );
};

const Track = styled.div<{ height: number }>`
  position: relative;
  width: 100%;
  height: ${({ height }) => height}px;
  border-radius: ${({ height }) => height}px;
  background-color: ${theme.colors.backgroundBasic.active};
`;

const ScaleFill = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;
  border-radius: ${({ height }) => height}px;
  transition: width 0.2s ease-in-out;
`;

const GaugeFill = styled.div<{ height: number }>`
  width: 100%;
  height: ${({ height }) => height}px;
  border-radius: ${({ height }) => height}px;
  background: ${NPS_GRADIENT};
`;

const Thumb = styled.div<{ height: number }>`
  position: absolute;
  top: 50%;
  width: ${({ height }) => height * 2}px;
  height: ${({ height }) => height * 2}px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background-color: ${theme.colors.textInverted.primary};
  border: 3px solid ${theme.colors.emotionWarning.default};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;
